import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Save, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { saveCompanyKnowledgeData } from "@/lib/knowledge-base-service";

// ─── Wizard steps (same questions as old wizard) ───────────────────────────

const STEPS = [
  {
    id: "basics",
    title: "Company basics",
    description: "Help the AI understand your organization at a glance.",
    questions: [
      { id: "companyName", type: "text", label: "Company name", placeholder: "e.g. Acme Corp" },
      {
        id: "industry",
        type: "select",
        label: "Industry",
        placeholder: "Select your industry",
        options: [
          { value: "technology", label: "Technology" },
          { value: "finance", label: "Finance & Banking" },
          { value: "healthcare", label: "Healthcare" },
          { value: "manufacturing", label: "Manufacturing" },
          { value: "retail", label: "Retail" },
          { value: "professional-services", label: "Professional Services" },
          { value: "education", label: "Education" },
          { value: "government", label: "Government & Public Sector" },
          { value: "nonprofit", label: "Nonprofit" },
          { value: "other", label: "Other" },
        ],
      },
      {
        id: "companySize",
        type: "select",
        label: "Company size",
        placeholder: "Select your team size",
        options: [
          { value: "1-10", label: "1–10 employees" },
          { value: "11-50", label: "11–50 employees" },
          { value: "51-200", label: "51–200 employees" },
          { value: "201-1000", label: "201–1,000 employees" },
          { value: "1000+", label: "1,000+ employees" },
        ],
      },
      { id: "foundedYear", type: "text", label: "Founded year (optional)", placeholder: "e.g. 2015" },
    ],
  },
  {
    id: "products",
    title: "Products & services",
    description: "What does your company do? This helps the AI give relevant policy advice.",
    questions: [
      {
        id: "whatWeDo",
        type: "textarea",
        rows: 4,
        label: "What does your company do?",
        placeholder: "Describe your business in a few sentences. What problem do you solve? Who are your customers?",
      },
      {
        id: "keyProducts",
        type: "textarea",
        rows: 3,
        label: "Key products or services",
        placeholder: "List your main offerings, product lines, or service areas.",
      },
    ],
  },
  {
    id: "culture",
    title: "Culture & values",
    description: "What makes your company unique? This shapes how policies are framed.",
    questions: [
      {
        id: "mission",
        type: "textarea",
        rows: 3,
        label: "Company mission",
        placeholder: "Brief description of your company's mission or purpose.",
      },
      {
        id: "values",
        type: "textarea",
        rows: 3,
        label: "Core values",
        placeholder: "e.g. Integrity, Innovation, Customer-first, Transparency...",
      },
      {
        id: "cultureNotes",
        type: "textarea",
        rows: 2,
        label: "Culture notes (optional)",
        placeholder: "e.g. Remote-first, flexible hours, DEI focus...",
      },
    ],
  },
  {
    id: "operations",
    title: "Operations & compliance",
    description: "How does your organization run? This helps with compliance and governance context.",
    questions: [
      {
        id: "departments",
        type: "textarea",
        rows: 2,
        label: "Key departments",
        placeholder: "e.g. Engineering, Sales, HR, Finance, Legal, Operations...",
      },
      {
        id: "complianceAreas",
        type: "textarea",
        rows: 2,
        label: "Key regulations or compliance areas",
        placeholder: "e.g. GDPR, SOC 2, ISO 27001, HIPAA...",
      },
      {
        id: "geography",
        type: "textarea",
        rows: 2,
        label: "Geographic presence",
        placeholder: "e.g. HQ in US, offices in UK and Germany, remote team globally...",
      },
    ],
  },
  {
    id: "extras",
    title: "Additional context",
    description: "Anything else the AI should know when writing policies.",
    questions: [
      {
        id: "acronyms",
        type: "textarea",
        rows: 2,
        label: "Important acronyms or terminology",
        placeholder: "e.g. RTO = Return to Office, PTO = Paid Time Off, internal product names...",
      },
      {
        id: "keyPeople",
        type: "textarea",
        rows: 2,
        label: "Key people or roles (optional)",
        placeholder: "e.g. CEO is John Smith, CTO leads engineering...",
      },
      {
        id: "anythingElse",
        type: "textarea",
        rows: 3,
        label: "Anything else the AI should know",
        placeholder: "Other context, constraints, or preferences for policy creation.",
      },
    ],
  },
];

interface CompanyProfileTabProps {
  companyId: string;
  initialAnswers: Record<string, string> | null;
}

export function CompanyProfileTab({ companyId, initialAnswers }: CompanyProfileTabProps) {
  const { toast } = useToast();
  const [answers, setAnswers] = useState<Record<string, string>>(initialAnswers ?? {});
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Sync when initialAnswers load
  useEffect(() => {
    if (initialAnswers) setAnswers(initialAnswers);
  }, [initialAnswers]);

  const step = STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;
  const progress = ((stepIndex + 1) / STEPS.length) * 100;
  const filledCount = Object.values(answers).filter(v => v?.trim()).length;

  const set = (id: string, value: string) =>
    setAnswers(prev => ({ ...prev, [id]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveCompanyKnowledgeData(companyId, answers);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      toast({ title: "Profile saved", description: "The AI agent will use this context when writing policies." });
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex items-start gap-4 pt-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">Company profile powers the AI agent</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              The AI policy agent draws from this profile when drafting policies, assessing compliance, and making recommendations. The more detail you provide, the more relevant its output.
            </p>
          </div>
          {filledCount > 0 && (
            <Badge variant="secondary" className="shrink-0 ml-auto">
              {filledCount} fields filled
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Step wizard */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
            <span>Step {stepIndex + 1} of {STEPS.length}</span>
            <span>{step.title}</span>
          </div>
          <Progress value={progress} className="h-1.5" />
          <CardTitle className="text-lg mt-3">{step.title}</CardTitle>
          {step.description && (
            <CardDescription>{step.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-5">
          {step.questions.map(q => (
            <div key={q.id} className="space-y-2">
              <Label htmlFor={q.id}>{q.label}</Label>
              {q.type === "text" && (
                <Input
                  id={q.id}
                  value={answers[q.id] ?? ""}
                  onChange={e => set(q.id, e.target.value)}
                  placeholder={q.placeholder}
                />
              )}
              {q.type === "textarea" && (
                <Textarea
                  id={q.id}
                  value={answers[q.id] ?? ""}
                  onChange={e => set(q.id, e.target.value)}
                  placeholder={q.placeholder}
                  rows={(q as any).rows ?? 3}
                  className="resize-y"
                />
              )}
              {q.type === "select" && (
                <Select value={answers[q.id] ?? ""} onValueChange={v => set(q.id, v)}>
                  <SelectTrigger id={q.id}>
                    <SelectValue placeholder={q.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {(q as any).options?.map((opt: { value: string; label: string }) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}

          {/* Navigation */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStepIndex(i => i - 1)}
              disabled={isFirst}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>

            <div className="flex gap-2">
              {!isLast ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setStepIndex(i => i + 1)}
                  className="gap-1"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : null}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="gap-1"
              >
                {saved ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Saved
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {saving ? "Saving…" : "Save profile"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
